"use client";

import { useEffect, useRef, useState } from "react";
import { MailCheck } from "lucide-react";
import type {
  BrochureDeliveryMethod,
  DomainConfig,
  Niche,
  SharedContent,
} from "../types";
import { pushDataLayer, sha256Hex } from "../lib/gtm";
import { getAttribution } from "../lib/attribution";
import Step1Contact from "./Step1Contact";
import Step2Address from "./Step2Address";
import Step3Enrichment from "./Step3Enrichment";
import TurnstileWidget from "./TurnstileWidget";

interface Props {
  domain: DomainConfig;
  shared: SharedContent;
  /** "" on the main page, a niche slug on niche pages */
  niche: "" | Niche;
  /** When true, render only the inner card (for embedding in the Hero). */
  compact?: boolean;
}

type Stage = 1 | 2 | 3 | "done";

interface Step1Data {
  first_name: string;
  email: string;
  phone: string;
}
interface Step2Data {
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
}
interface Step3Data {
  solution_type: string;
  budget_range: string;
  timeframe: string;
  special_requirements: string;
}

function genEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Lees een cookie-waarde uit document.cookie. Returns undefined als niet gezet. */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`));
  return match?.[1];
}

/**
 * Vergaar Meta CAPI-relevante client signals: _fbp + _fbc cookies + UA.
 * Worden samen met de step-submit naar de server gestuurd zodat de
 * Edge Function ze 1-op-1 kan doorzetten naar Meta voor server-side
 * matching + ad-click attribution.
 */
function gatherMetaClientHints(): {
  _fbp?: string;
  _fbc?: string;
  user_agent?: string;
} {
  const out: { _fbp?: string; _fbc?: string; user_agent?: string } = {};
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  if (fbp) out._fbp = fbp;
  if (fbc) out._fbc = fbc;
  if (typeof navigator !== "undefined" && navigator.userAgent) {
    out.user_agent = navigator.userAgent;
  }
  return out;
}

/**
 * Slugify een domein-naam voor gebruik in een GTM formName fallback.
 * "badkamer-wijzer.nl" → "badkamer_wijzer"
 */
function defaultFormName(domain: string): string {
  const stem = domain.split(".")[0] ?? "brochure";
  return `form_brochure_${stem.replace(/-/g, "_")}`;
}

/**
 * Three-step progressive brochure form.
 * Step 1 commits the lead row immediately (so abandoned leads 2/3 are still
 * captured). Steps 2 + 3 PATCH the same row by lead_id (kept in localStorage,
 * scoped per brochure_page_id so users filling multiple domains don't cross).
 */
export default function BrochureForm({ domain, shared, niche, compact = false }: Props) {
  const localKey = `swiftflow_brochure_lead_id:${domain.brochure_page_id}`;
  const labels = shared.form_labels;

  const [stage, setStage] = useState<Stage>(1);
  const [leadId, setLeadId] = useState<string | null>(null);
  // Bezorging is nu altijd digitaal (email). Physical-flow is gedeactiveerd —
  // we mailen een link naar de brochure-portal. State blijft bestaan voor
  // backwards-compat met de step-2/3 PATCH bodies.
  const [delivery] = useState<BrochureDeliveryMethod>("digital");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cloudflare Turnstile token — alleen ingevuld als NEXT_PUBLIC_TURNSTILE_SITE_KEY
  // is gezet, anders blijft 'm null en backend skipt verificatie ook.
  const captchaTokenRef = useRef<string | null>(null);

  const [s1, setS1] = useState<Step1Data>({ first_name: "", email: "", phone: "" });
  const [s2, setS2] = useState<Step2Data>({
    street: "",
    house_number: "",
    postal_code: "",
    city: "",
  });
  const [s3, setS3] = useState<Step3Data>({
    solution_type: "",
    budget_range: "",
    timeframe: "",
    special_requirements: "",
  });

  // Restore lead_id on mount + fire page_view
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(localKey);
    if (stored) setLeadId(stored);
    pushDataLayer({
      event: "brochure_page_view",
      page_id: domain.brochure_page_id,
      page_domain: domain.domain,
      niche: niche || "",
    });
  }, [localKey, domain.brochure_page_id, domain.domain, niche]);

  async function submitStep1(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    const event_id = genEventId();
    try {
      const attribution = getAttribution() ?? {};
      const metaHints = gatherMetaClientHints();
      const res = await fetch("/api/brochure-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: 1,
          first_name: s1.first_name.trim(),
          email: s1.email.trim().toLowerCase(),
          phone: s1.phone.replace(/\s/g, ""),
          delivery_method: delivery,
          event_id,
          niche,
          source_url: typeof window !== "undefined" ? window.location.href : "",
          honeypot: "",
          ...(captchaTokenRef.current ? { captcha_token: captchaTokenRef.current } : {}),
          ...attribution,
          ...metaHints,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const { lead_id } = (await res.json()) as { lead_id: string };
      setLeadId(lead_id);
      window.localStorage.setItem(localKey, lead_id);

      const phoneDigits = s1.phone.replace(/\D/g, "");
      const hashed = await sha256Hex(s1.email);
      const hashedPhone = phoneDigits ? await sha256Hex(phoneDigits) : undefined;
      const hashedFn = s1.first_name.trim()
        ? await sha256Hex(s1.first_name)
        : undefined;
      // Manual Advanced Matching — re-init pixel met user data zodra we het
      // hebben (sluit Diagnostics 'set up manual advanced matching' issue af).
      // Wordt door Meta gemerged met de bestaande pixel session.
      if (window.fbq) {
        window.fbq("init", domain.meta_pixel_id ?? "", {
          em: s1.email.trim().toLowerCase(),
          ...(phoneDigits ? { ph: phoneDigits } : {}),
          ...(s1.first_name ? { fn: s1.first_name.trim().toLowerCase() } : {}),
        });
      }
      // Lead event MET currency + value — sluit Diagnostics 'send valid currency
      // information for website Lead events' issue af (5% hogere ROAS volgens
      // Meta's modeling). Value komt uit conversion_values config; default €50
      // voor brochure-aanvraag (industry-typical lead value voor renovatie).
      const leadValue =
        domain.conversion_values?.brochure_aanvraag_value ?? 50;
      const leadCurrency = domain.conversion_values?.currency ?? "EUR";
      if (window.fbq) {
        window.fbq(
          "track",
          "Lead",
          { value: leadValue, currency: leadCurrency },
          { eventID: event_id },
        );
      }
      const step1Event: import("../lib/gtm").BrochureStepEvent = {
        event: "brochure_step_1_complete",
        page_id: domain.brochure_page_id,
        lead_id,
        event_id,
        delivery_method: delivery,
        lead_hashed_email: hashed,
      };
      if (hashedPhone) step1Event.lead_hashed_phone = hashedPhone;
      if (hashedFn) step1Event.lead_hashed_first_name = hashedFn;
      // _fbp/_fbc lezen we al voor de API-body (gatherMetaClientHints) maar
      // pushen we ook naar de dataLayer zodat de Server GTM Facebook CAPI
      // tag ze via een Event Data-variable kan lezen i.p.v. cookie-lookup.
      if (metaHints._fbp) step1Event._fbp = metaHints._fbp;
      if (metaHints._fbc) step1Event._fbc = metaHints._fbc;
      if (niche) step1Event.niche = niche;
      // Stap 1 IS het conversion-moment voor Google Ads (lead is binnen).
      // Value moet hier mee zodat Smart Bidding de juiste lead-waarde krijgt.
      const cv1 = domain.conversion_values;
      if (cv1 && typeof cv1.brochure_aanvraag_value === "number") {
        step1Event.value = cv1.brochure_aanvraag_value;
        step1Event.currency = cv1.currency;
      }
      pushDataLayer(step1Event);

      // Zwaluw GTM-shim: parallel formSubmission event zodat een GTM
      // container die op de Zwaluw-naming triggert (formSubmission +
      // formName + formId) onze step-1 lead ook kan oppakken.
      pushDataLayer({
        event: "formSubmission",
        formName: domain.gtm_form_name ?? defaultFormName(domain.domain),
        formId: "1",
        event_id,
        page_id: domain.brochure_page_id,
        lead_id,
      });

      setStage(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitStep2(e: React.FormEvent) {
    e.preventDefault();
    if (!leadId || submitting) return;
    setError(null);
    setSubmitting(true);
    const event_id = genEventId();
    try {
      const metaHints = gatherMetaClientHints();
      const res = await fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: 2,
          street: s2.street.trim(),
          house_number: s2.house_number.trim(),
          postal_code: s2.postal_code.replace(/\s/g, "").toUpperCase(),
          city: s2.city.trim(),
          event_id,
          ...metaHints,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // Standard event SubmitApplication ipv custom 'BrochureStep2' — Meta
      // whitelist accepteert custom events alleen na review, en Diagnostics
      // markeerde 'Custom events blocked'. SubmitApplication past semantisch
      // (gebruiker dient adres in voor brochure-aanvraag).
      if (window.fbq) window.fbq("track", "SubmitApplication", {}, { eventID: event_id });
      const postalNorm = s2.postal_code.replace(/\s/g, "").toLowerCase();
      const phoneDigits2 = s1.phone.replace(/\D/g, "");
      const step2Event: import("../lib/gtm").BrochureStepEvent = {
        event: "brochure_step_2_complete",
        page_id: domain.brochure_page_id,
        lead_id: leadId,
        event_id,
        postal_code_prefix: s2.postal_code.replace(/\s/g, "").slice(0, 4),
      };
      // Carry-forward van step-1 user_data zodat de Server GTM CAPI tag voor
      // SubmitApplication dezelfde matching-velden heeft als de Lead-event.
      // s1-state staat nog in memory (component blijft mounted door alle 3 stages).
      if (s1.email) step2Event.lead_hashed_email = await sha256Hex(s1.email);
      if (phoneDigits2)
        step2Event.lead_hashed_phone = await sha256Hex(phoneDigits2);
      if (s1.first_name.trim())
        step2Event.lead_hashed_first_name = await sha256Hex(s1.first_name);
      if (postalNorm)
        step2Event.lead_hashed_postal_code = await sha256Hex(postalNorm);
      if (metaHints._fbp) step2Event._fbp = metaHints._fbp;
      if (metaHints._fbc) step2Event._fbc = metaHints._fbc;
      if (niche) step2Event.niche = niche;
      pushDataLayer(step2Event);

      pushDataLayer({
        event: "formSubmission",
        formName: domain.gtm_form_name ?? defaultFormName(domain.domain),
        formId: "2",
        event_id,
        page_id: domain.brochure_page_id,
        lead_id: leadId,
      });

      setStage(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function skipStep2Digital() {
    // delivery is al "digital" (default) — we slaan alleen step_completed op.
    if (leadId) {
      void fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: 2,
          delivery_method: "digital",
          event_id: genEventId(),
        }),
      });
    }
    setStage(3);
  }

  async function submitStep3(e: React.FormEvent) {
    e.preventDefault();
    if (!leadId || submitting) return;
    setError(null);
    setSubmitting(true);
    const event_id = genEventId();
    try {
      const metaHints = gatherMetaClientHints();
      const payload: Record<string, unknown> = { step: 3, event_id };
      if (s3.solution_type) payload["solution_type"] = s3.solution_type;
      if (s3.budget_range) payload["budget_range"] = s3.budget_range;
      if (s3.timeframe) payload["timeframe"] = s3.timeframe;
      if (s3.special_requirements.trim()) {
        payload["special_requirements"] = s3.special_requirements.trim();
      }
      Object.assign(payload, metaHints);
      const res = await fetch(`/api/brochure-lead?lead_id=${encodeURIComponent(leadId)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      // Standard event CompleteRegistration ipv custom 'BrochureStep3' —
      // het volledige funnel-proces is nu compleet (basis info + adres +
      // enrichment), past semantisch bij Meta's CompleteRegistration event.
      if (window.fbq) window.fbq("track", "CompleteRegistration", {}, { eventID: event_id });
      const phoneDigits3 = s1.phone.replace(/\D/g, "");
      const postalNorm3 = s2.postal_code.replace(/\s/g, "").toLowerCase();
      const evt: import("../lib/gtm").BrochureStepEvent = {
        event: "brochure_step_3_complete",
        page_id: domain.brochure_page_id,
        lead_id: leadId,
        event_id,
      };
      // Carry-forward van user_data uit step 1 + 2 zodat de Server GTM CAPI
      // tag voor CompleteRegistration dezelfde match-velden heeft als de
      // eerdere events. Voorkomt match-quality drop op de funnel-end event.
      if (s1.email) evt.lead_hashed_email = await sha256Hex(s1.email);
      if (phoneDigits3)
        evt.lead_hashed_phone = await sha256Hex(phoneDigits3);
      if (s1.first_name.trim())
        evt.lead_hashed_first_name = await sha256Hex(s1.first_name);
      if (postalNorm3)
        evt.lead_hashed_postal_code = await sha256Hex(postalNorm3);
      if (metaHints._fbp) evt._fbp = metaHints._fbp;
      if (metaHints._fbc) evt._fbc = metaHints._fbc;
      if (niche) evt.niche = niche;
      if (s3.solution_type) evt.solution_type = s3.solution_type;
      if (s3.budget_range) evt.budget_range = s3.budget_range;
      if (s3.timeframe) evt.timeframe = s3.timeframe;
      // Stap 3 is enrichment, GEEN extra Google Ads conversion meer:
      // de conversie + value zijn al gefired bij stap 1 (lead-moment).
      // Dubbel-tellen via GTM-trigger op step_1_or_3 moest sowieso weg.
      pushDataLayer(evt);

      pushDataLayer({
        event: "formSubmission",
        formName: domain.gtm_form_name ?? defaultFormName(domain.domain),
        formId: "3",
        event_id,
        page_id: domain.brochure_page_id,
        lead_id: leadId,
      });

      window.localStorage.removeItem(localKey);
      // Onderdruk ExitIntentPopup na succesvolle submit
      try { sessionStorage.setItem("bw_exit_intent_seen_v1", "1"); } catch { /* ignore */ }
      setStage("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function skipStep3() {
    if (leadId) window.localStorage.removeItem(localKey);
    try { sessionStorage.setItem("bw_exit_intent_seen_v1", "1"); } catch { /* ignore */ }
    setStage("done");
  }

  const card = (
    <div className="bg-white rounded-card shadow-card-hover p-6 md:p-8 ring-1 ring-stone-100">
      <ProgressBar stage={stage} />

      {error ? (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-terracotta-100 bg-terracotta-50 px-4 py-3 text-sm text-terracotta-700"
        >
          {error}
        </div>
      ) : null}

      {stage === 1 ? (
        <>
          <Step1Contact
            labels={labels}
            data={s1}
            onChange={setS1}
            onSubmit={submitStep1}
            submitting={submitting}
          />
          {/* Cloudflare Turnstile — invisible voor 99% van gebruikers, toont
              alleen mini-challenge bij verdacht gedrag. Vult captchaTokenRef
              die meegestuurd wordt bij submit. */}
          <TurnstileWidget tokenRef={captchaTokenRef} />
        </>
      ) : null}

      {stage === 2 ? (
        <Step2Address
          labels={labels}
          data={s2}
          onChange={setS2}
          onSubmit={submitStep2}
          onSkipDigital={skipStep2Digital}
          submitting={submitting}
        />
      ) : null}

      {stage === 3 ? (
        <Step3Enrichment
          labels={labels}
          data={s3}
          onChange={setS3}
          onSubmit={submitStep3}
          onSkip={skipStep3}
          submitting={submitting}
        />
      ) : null}

      {stage === "done" ? (
        <div className="text-center py-6">
          <div
            className="mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center rounded-full"
            style={{
              background: "var(--color-secondary-light, #D3DDE8)",
              color: "var(--color-secondary, #1E3A5F)",
            }}
          >
            <MailCheck size={32} strokeWidth={1.75} aria-hidden />
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-ink mb-3">
            {labels.success_headline}
          </h2>
          <p className="text-stone-700 leading-relaxed">{labels.success_body}</p>
        </div>
      ) : null}
    </div>
  );

  if (compact) return card;

  return (
    <section
      id="brochure-form"
      className="py-14 md:py-20 bg-gradient-to-b from-cream to-cream-100 scroll-mt-4"
    >
      <div className="max-w-2xl mx-auto px-6">{card}</div>
    </section>
  );
}

function ProgressBar({ stage }: { stage: Stage }) {
  const num = stage === "done" ? 4 : stage;
  return (
    <div className="flex items-center gap-2 mb-6" aria-label={`Stap ${num} van 3`}>
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={`flex-1 h-1.5 rounded-full transition-colors ${
            num === n ? "bg-terracotta" : num > n ? "" : "bg-stone-100"
          }`}
          style={
            num > n
              ? { background: "var(--color-secondary, #1E3A5F)" }
              : undefined
          }
        />
      ))}
    </div>
  );
}
